import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Check, X } from 'lucide-react';

interface WelcomeUpgradeModalProps {
  businessId?: string;
  onClose: () => void;
}

const animationConfig = {
  backdrop: { bg: 'rgba(0, 0, 0, 0.04)', blur: '8px' },
  duration: 400,
};

export function WelcomeUpgradeModal({ onClose }: WelcomeUpgradeModalProps) {
  const [phase, setPhase] = useState<'entering' | 'visible' | 'exiting' | 'hidden'>('hidden');
  const config = animationConfig;

  useEffect(() => {
    setPhase('entering');
    const timer = setTimeout(() => setPhase('visible'), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(() => {
    setPhase('exiting');
    setTimeout(() => {
      onClose();
    }, config.duration);
  }, [onClose, config.duration]);

  const showContent = phase !== 'hidden';
  if (!showContent && phase === 'hidden') return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        onClick={handleClose}
        className="absolute inset-0"
        style={{
          backgroundColor: phase === 'visible' ? config.backdrop.bg : 'rgba(0, 0, 0, 0)',
          backdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
          WebkitBackdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
          transition: `background-color ${config.duration}ms ease-out, backdrop-filter ${config.duration}ms ease-out`,
        }}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md"
        style={{
          opacity: phase === 'visible' ? 1 : 0,
          transform: phase === 'visible' ? 'scale(1)' : 'scale(0.98)',
          transition: `opacity ${config.duration}ms ease-out, transform ${config.duration}ms ease-out`,
        }}
      >
        <div className="relative bg-white dark:bg-zinc-900 rounded-[32px] shadow-2xl overflow-hidden">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center transition-colors"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex flex-col">
            {/* Left side - Celebratory illustration */}
            <div className="bg-zinc-50 dark:bg-zinc-800/50 p-6 md:p-8 flex items-center justify-center relative overflow-hidden">
              {/* Falling confetti */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute w-2 h-2 rounded-full bg-primary/50 animate-[confetti-fall_3s_linear_infinite]" style={{ left: '10%', animationDelay: '-1.2s' }} />
                <div className="absolute w-1.5 h-1.5 rounded-full bg-amber-400/60 animate-[confetti-fall_3.5s_linear_infinite]" style={{ left: '25%', animationDelay: '-2.8s' }} />
                <div className="absolute w-2.5 h-1 rounded-full bg-emerald-400/50 animate-[confetti-fall_2.8s_linear_infinite]" style={{ left: '42%', animationDelay: '-0.5s' }} />
                <div className="absolute w-1.5 h-1.5 rounded-full bg-violet-400/50 animate-[confetti-fall_3.2s_linear_infinite]" style={{ left: '58%', animationDelay: '-2.1s' }} />
                <div className="absolute w-2 h-1 rounded-full bg-rose-400/50 animate-[confetti-fall_3.8s_linear_infinite]" style={{ left: '72%', animationDelay: '-1.6s' }} />
                <div className="absolute w-1.5 h-2 rounded-full bg-primary/40 animate-[confetti-fall_3.1s_linear_infinite]" style={{ left: '88%', animationDelay: '-0.8s' }} />
                <div className="absolute w-2 h-1.5 rounded-full bg-amber-400/45 animate-[confetti-fall_3.6s_linear_infinite]" style={{ left: '5%', animationDelay: '-3.2s' }} />
                <div className="absolute w-1.5 h-1.5 rounded-full bg-emerald-400/55 animate-[confetti-fall_2.9s_linear_infinite]" style={{ left: '35%', animationDelay: '-1.9s' }} />
                <div className="absolute w-2 h-1 rounded-full bg-violet-400/45 animate-[confetti-fall_3.4s_linear_infinite]" style={{ left: '65%', animationDelay: '-2.5s' }} />
                <div className="absolute w-1.5 h-1.5 rounded-full bg-rose-400/40 animate-[confetti-fall_3.3s_linear_infinite]" style={{ left: '80%', animationDelay: '-0.3s' }} />
                <div className="absolute w-2 h-2 rounded-full bg-primary/35 animate-[confetti-fall_3.7s_linear_infinite]" style={{ left: '18%', animationDelay: '-3.0s' }} />
                <div className="absolute w-1.5 h-1 rounded-full bg-amber-400/50 animate-[confetti-fall_3s_linear_infinite]" style={{ left: '50%', animationDelay: '-1.4s' }} />
                <div className="absolute w-2 h-1.5 rounded-full bg-emerald-400/45 animate-[confetti-fall_2.7s_linear_infinite]" style={{ left: '92%', animationDelay: '-2.3s' }} />
                <div className="absolute w-1.5 h-1.5 rounded-full bg-violet-400/50 animate-[confetti-fall_3.9s_linear_infinite]" style={{ left: '15%', animationDelay: '-0.9s' }} />
                <div className="absolute w-2 h-1 rounded-full bg-rose-400/45 animate-[confetti-fall_3.2s_linear_infinite]" style={{ left: '48%', animationDelay: '-2.7s' }} />
                <div className="absolute w-1.5 h-2 rounded-full bg-primary/45 animate-[confetti-fall_3.5s_linear_infinite]" style={{ left: '78%', animationDelay: '-1.7s' }} />
              </div>

              {/* Invoice card illustration */}
              <div className="w-full max-w-[240px] relative">
                <div className="rounded-2xl bg-white dark:bg-zinc-900 p-5 shadow-xl border border-zinc-200/60 dark:border-zinc-700/60">
                  {/* Invoice header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center">
                        <div className="w-3 h-2 rounded-sm bg-zinc-400 dark:bg-zinc-500" />
                      </div>
                      <div className="w-14 h-2 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                    </div>
                    <div className="text-[10px] font-medium text-zinc-400 dark:text-zinc-500">INV-001</div>
                  </div>

                  {/* Bill to skeleton */}
                  <div className="mb-4">
                    <div className="w-10 h-1 rounded-full bg-zinc-200 dark:bg-zinc-700 mb-2" />
                    <div className="w-20 h-1.5 rounded-full bg-zinc-300 dark:bg-zinc-600 mb-1" />
                    <div className="w-16 h-1 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                  </div>

                  {/* Line items */}
                  <div className="border-t border-zinc-100 dark:border-zinc-800 pt-3 mb-3 space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="w-24 h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                      <div className="w-10 h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="w-20 h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                      <div className="w-8 h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                    </div>
                  </div>

                  {/* Total row */}
                  <div className="border-t border-zinc-200 dark:border-zinc-700 pt-3 flex justify-between items-center">
                    <div className="w-8 h-1.5 rounded-full bg-zinc-300 dark:bg-zinc-600" />
                    <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">$0.00</div>
                  </div>

                  {/* Green check badge — overlapping bottom-right */}
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                    <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Confetti animation */}
            <style>{`
              @keyframes confetti-fall {
                0% { transform: translateY(-10px) translateX(0px) rotate(0deg); opacity: 0; }
                10% { opacity: 0.7; }
                90% { opacity: 0.5; }
                100% { transform: translateY(400px) translateX(20px) rotate(360deg); opacity: 0; }
              }
            `}</style>

            {/* Right side - Welcome message */}
            <div className="px-8 py-8 md:py-10 flex flex-col justify-center">
              <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
                Welcome to Invoice Owl!
              </h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6">
                Your account is ready and your subscription is active. Start sending invoices and getting paid.
              </p>

              <div className="space-y-2.5 mb-6">
                {['Your subscription is active', 'Your plan is ready to use today', 'Cancel anytime, no questions asked'].map((perk) => (
                  <div key={perk} className="flex items-center gap-2.5">
                    <Check className="h-4 w-4 text-zinc-400 dark:text-zinc-500 flex-shrink-0" />
                    <span className="text-sm text-zinc-600 dark:text-zinc-400">{perk}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <Button
                className="w-full h-11"
                onClick={handleClose}
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
